import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertDialogSlide, toast } from '@sales-center/components'
import { useSelector } from 'react-redux'
import { useUserSearch } from '@user/store/users/hooks/useUserSearch'
import { updateLoadingState } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useDeleteUser } from './hooks/useDeleteUser'
import { useCreateUser } from './hooks/useCreateUser'
import { useUpdateUser } from './hooks/useUpdateUser'
import {
  ICheckEmail,
  ITenantUser,
  ITenantUserUpdate,
  IUserUsageDetails,
} from '@user/store/users/typings'
import {
  selectEmailUniquenessMap,
  selectUsersFilters,
  selectUsersTotalCount,
  setUsersPage,
} from '@user/store/users/slice'
import {
  checkUniqueMail,
  getUsageDetails,
  getUserEmailUniqueness,
} from '../../../store/users/api'
import { useGetUser } from '../../../store/users/hooks/useGetUser'
import { Formik, FormikHelpers } from 'formik'
import {
  IFormikUserModel,
  userFormInitialValues,
  validateMappedUserForm,
  validateUnmappedUserForm,
} from '../Users/UpdateUser/typings'
import { useGetRolesOnMount } from '../../../store/roles/hooks/useGetRolesOnMount'
import { selectAllRoles } from '@user/store/roles/slice'
import { Role } from '@user/store/roles/typings'
import { useGetSalesTeamListOnMount } from '../../../store/salesTeam/hooks/useGetSalesTeamListOnMount'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { adminRoleName, guestRoleName } from '../Users/UpdateUser/constants'
import { getEmployee } from '@user/store/employee'
import { useGetLicenseInfo } from './hooks/useGetLicenseInfo'
import { UsersNew } from '@user/components/Settings/Users/UsersNew/UsersNew'
import { UpdateUserSideBarNew } from './UpdateUser/UpdateUserSideBarNew'
import { ISalesTeam } from '@user/store/salesTeam'

const Users = () => {
  const dispatch = useAppDispatch()

  //========== S T A T E ==========
  const [addDisabled, setAddDisabled] = useState<boolean>(true)
  const [addDisabledText, setAddDisabledText] = useState<string>('')
  const [deleteUser, setDeleteUser] = useState<ITenantUser | undefined>()
  const [deleteUserMessage, setDeleteUserMessage] = useState<string>('')
  const [editFormVariant, setEditFormVariant] = useState<
    'mapped' | 'unmapped'
  >()
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const [searchText, setSearchText] = useState<string>('')
  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false)
  const [showDeleteProhibitedAlert, setshowDeleteProhibitedAlert] =
    useState<boolean>(false)
  const [licenseLeft, setLicenseLeft] = useState<number | undefined>(undefined)
  const [isAddMode, setIsAddMode] = useState<boolean>(false)
  const [hasFormErrors, setHasFormErrors] = useState<boolean>(false)
  const [initialActive, setInitialActive] = useState<boolean>(false)

  const { page, perPage, orderBy } = useSelector(selectUsersFilters)
  const totalCount = useSelector(selectUsersTotalCount)
  const {
    users: tenantUsers,
    status,
    reload: reloadUsers,
  } = useUserSearch(searchText, (page - 1) * perPage, perPage, orderBy)
  //========== L O A D   F R O M  S T O R E ==========
  const rolesLoadingStatus = useGetRolesOnMount()
  const salesTeamsLoadingStatus = useGetSalesTeamListOnMount()
  const roles: Role[] = useSelector(selectAllRoles)
  const salesTeams: ISalesTeam[] = useSelector(selectAllSalesTeams)

  const emailIsUniqueMap = useSelector(selectEmailUniquenessMap)

  useEffect(() => {
    if (!emailIsUniqueMap || Object.keys(emailIsUniqueMap).length === 0) {
      dispatch(getUserEmailUniqueness())
    }
  }, [emailIsUniqueMap, dispatch])

  const loadPending =
    rolesLoadingStatus === 'pending' || salesTeamsLoadingStatus === 'pending'

  const {
    status: editUserStatus,
    user: editUser,
    setUserId: setEditUserId,
  } = useGetUser(true, true)

  const resetFormOnSuccess = useCallback(
    (formikHelpers: FormikHelpers<any>) => {
      formikHelpers.resetForm()
      setSidePanelOpen(false)
      setEditUserId(undefined)
      setIsAddMode(false)
    },
    [setEditUserId],
  )

  const handleDeleteSuccess = useCallback(() => {
    const maxPage = Math.max(1, Math.ceil(totalCount / perPage))
    if (page > maxPage) {
      dispatch(setUsersPage(maxPage))
    } else {
      reloadUsers()
    }
  }, [totalCount, perPage, page, dispatch, reloadUsers])

  const { status: deleteStatus, execute: executeDelete } =
    useDeleteUser(handleDeleteSuccess)
  const { status: createStatus, execute: executeCreate } = useCreateUser(
    setHasFormErrors,
    resetFormOnSuccess,
    reloadUsers,
  )
  const { status: updateStatus, execute: executeUpdate } = useUpdateUser(
    reloadUsers,
    resetFormOnSuccess,
  )

  const {
    status: getLicensingStatus,
    execute: getLicensingInfo,

    mode,
  } = useGetLicenseInfo(licenseLeft, setLicenseLeft)

  useEffect(() => {
    if (isAddMode) {
      setEditUserId(undefined)
      setEditFormVariant(mode)
      setSidePanelOpen(true)
    }
  }, [isAddMode, licenseLeft, mode, setEditUserId])

  const handleDeleteConfirm = () => {
    if (deleteUser) {
      executeDelete(deleteUser.id)
      setShowDeleteAlert(false)
    }
  }

  const handleDeleteRequest = useCallback(
    async (userId: string) => {
      //========== C H E C K   I F   U S E R   I S   A S S I G N E D   T O   A N Y   L E A D S ==========
      if (userId) {
        const result = await dispatch(getUsageDetails(userId))
        if (getUsageDetails.fulfilled.match(result)) {
          const usageDetails: IUserUsageDetails = result.payload

          const recordCount = usageDetails.leadCount + usageDetails.oppCount

          if (recordCount > 0) {
            // user is assigned to leads - prohibit delete
            const message = `There ${
              recordCount === 1 ? 'is 1 record' : `are ${recordCount} records`
            } assigned to this user. All leads and opportunities must be reassigned before deleting this user.`
            setDeleteUserMessage(message)
            setshowDeleteProhibitedAlert(true)
          } else {
            const userToDelete = tenantUsers.find((u) => u.id === userId)
            setDeleteUser(userToDelete)
            let message: string = 'Are you sure you want to delete this user?'
            if (usageDetails.salesTeamAssignmentsCount > 0) {
              message += ` This user will be removed from ${
                usageDetails.salesTeamAssignmentsCount
              } sales team${
                usageDetails.salesTeamAssignmentsCount !== 1 ? 's.' : '.'
              }`
            }
            setDeleteUserMessage(message)
            setShowDeleteAlert(true)
          }
        }
      }
    },
    [dispatch, tenantUsers],
  )

  const handleEditClick = useCallback(
    (userId: string, initialActive?: boolean): void => {
      getLicensingInfo(mode, true)
      setEditUserId(userId)
      if (initialActive) {
        setInitialActive(true)
      }
    },
    [getLicensingInfo, mode, setEditUserId],
  )

  const handleUpdateFromPPRequest = useCallback(
    async (externalUserId: string) => {
      if (externalUserId) {
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(getEmployee(externalUserId))

        if (getEmployee.fulfilled.match(result)) {
          const userToUpdate = tenantUsers.find(
            (u) => u.externalUserId === externalUserId,
          )
          if (userToUpdate) {
            const userPushModel = { ...userToUpdate }
            const employee = result.payload

            userPushModel.firstName = employee.firstName
            userPushModel.lastName = employee.lastName
            userPushModel.emailAddress = employee.workWaveId

            const role = roles.find((x) => x.id == userPushModel.roleId)
            if (role) executeUpdate({ ...userPushModel, role: { ...role } })
            dispatch(updateLoadingState('hidden'))
          }
        } else {
          dispatch(updateLoadingState('hidden'))
        }
      }
    },
    [dispatch, tenantUsers, executeUpdate, roles],
  )

  const handleSubmitUserForm = useCallback(
    async (model: IFormikUserModel, formikHelpers: FormikHelpers<any>) => {
      const { setErrors, setSubmitting } = formikHelpers
      if (
        !(model.id && model.id.length > 0) ||
        model.emailAddress?.trim().toLowerCase() !=
          editUser?.emailAddress?.trim().toLowerCase()
      ) {
        const input: ICheckEmail = {
          email: model.emailAddress,
          userId: undefined,
          withSSOCheck:
            !!editUser && model.emailAddress != editUser?.emailAddress,
        }
        try {
          const result = await dispatch(checkUniqueMail(input)).unwrap()
          if (!result.isUnique) {
            toast.error(result.errorMessage)

            setSubmitting(false)
            return
          }
        } catch (err) {
          setErrors({
            emailAddress: 'Error checking email',
          })
          setSubmitting(false)
          return
        }
      }

      const user: ITenantUserUpdate = {
        emailAddress: model.emailAddress,
        externalUserId: model.externalUser?.id ?? '',
        firstName: model.firstName,
        id: model.id,
        lastName: model.lastName,
        role: model.role,
        roleId: model.role?.id ?? -1,
        isActive: model.isActive,
        salesTeamPositionIds:
          model.role?.name === adminRoleName ||
          model.role?.name === guestRoleName
            ? []
            : model.salesTeamAssignments.map((a) =>
                parseInt(a.salesTeamPositionId),
              ),
        disabledAssignmentRulesCount: null,
      }
      if (user.id && user.id.length > 0) {
        if (editUser && editUser.emailAddress !== user.emailAddress)
          user.emailUpdated = true
        // update
        executeUpdate(user, true, formikHelpers)
      } else {
        // add
        executeCreate(user, formikHelpers)
      }
    },
    [dispatch, editUser, executeUpdate, executeCreate],
  )

  const updateUserStatus = useCallback(
    async (model: ITenantUser, reloadUsers?: boolean) => {
      const user: ITenantUserUpdate = {
        emailAddress: model.emailAddress,
        externalUserId: model.externalUserId ?? '',
        firstName: model.firstName,
        id: model.id,
        lastName: model.lastName,
        role: roles.find((role) => role.id === model.roleId),
        roleId: model.roleId ?? -1,
        isActive: model.isActive,
        salesTeamPositionIds:
          model.roleName === adminRoleName || model.roleName === guestRoleName
            ? []
            : model.salesTeamPositionIds,
        disabledAssignmentRulesCount: null,
      }
      executeUpdate(user, reloadUsers)
    },
    [roles, executeUpdate],
  )

  const handleUpdateUserClose = useCallback(() => {
    setSidePanelOpen(false)
    setEditUserId(undefined)
    setLicenseLeft(undefined)
    setIsAddMode(false)
  }, [setEditUserId])

  //show loading state
  useEffect(() => {
    if (
      status == 'pending' ||
      createStatus == 'pending' ||
      deleteStatus == 'pending' ||
      editUserStatus == 'pending' ||
      updateStatus == 'pending' ||
      getLicensingStatus == 'pending'
    ) {
      dispatch(updateLoadingState('visible'))
    } else {
      if (createStatus == 'fail' || hasFormErrors) {
        setSidePanelOpen(true)
      }
      if (
        createStatus == 'success' ||
        updateStatus == 'success' ||
        deleteStatus == 'success'
      ) {
        // setResetLicenseinfo(true)
      }
      dispatch(updateLoadingState('hidden'))
    }
  }, [
    createStatus,
    deleteStatus,
    dispatch,
    editUserStatus,
    getLicensingStatus,
    hasFormErrors,
    status,
    updateStatus,
  ])

  useEffect(() => {
    if (editUser) {
      if (editUser.externalUserId) {
        setEditFormVariant('mapped')
      } else {
        setEditFormVariant('unmapped')
      }
      setSidePanelOpen(true)
    }
  }, [editUser])

  useEffect(() => {
    const addDisabled =
      !salesTeams || salesTeams.length == 0 || !roles || roles.length == 0
    setAddDisabled(addDisabled)
    setAddDisabledText(
      addDisabled
        ? 'You must have at least one Role and one Sales Team to add a User'
        : '',
    )
  }, [roles, salesTeams])

  const disableDeleteTooltip = useMemo(
    () =>
      (user: ITenantUser): string | undefined => {
        if (!!user.leadCount || !!user.oppCount) {
          const recordCount = (user?.leadCount ?? 0) + (user?.oppCount ?? 0)
          return `You must re-assign ${
            recordCount === 1 ? 'the record' : 'all records'
          } assigned to this user before they can be deleted`
        } else if (user.isSuperAdmin) {
          return 'You cannot delete the Super Admin created by the system'
        }
        return undefined
      },
    [],
  )

  return (
    <>
      <UsersNew
        usersLoading={status === 'pending'}
        loadPending={loadPending}
        addDisabled={addDisabled}
        addDisabledText={addDisabledText}
        rolesLoadingStatus={rolesLoadingStatus}
        getLicensingInfo={getLicensingInfo}
        rows={tenantUsers}
        handleEditClick={handleEditClick}
        handleDeleteRequest={handleDeleteRequest}
        disableDeleteTooltip={disableDeleteTooltip}
        handleUpdateFromPPRequest={handleUpdateFromPPRequest}
        setAddMode={setIsAddMode}
        updateUserStatus={updateUserStatus}
        setSearchText={setSearchText}
      />

      {/* ========== U P D A T E   U S E R   S I D E   B A R ========== */}
      {sidePanelOpen && (
        <Formik
          enableReinitialize
          initialValues={userFormInitialValues}
          onSubmit={handleSubmitUserForm}
          validate={
            editFormVariant == 'mapped'
              ? validateMappedUserForm
              : validateUnmappedUserForm
          }
        >
          <UpdateUserSideBarNew
            onClose={handleUpdateUserClose}
            open={sidePanelOpen}
            user={editUser}
            variant={editFormVariant}
            licenseLeft={licenseLeft}
            initialActive={initialActive}
            setInitialActive={setInitialActive}
          />
        </Formik>
      )}

      {/* ========== D E L E T E  P R O H I B I T E D   A L E R T ========== */}
      <AlertDialogSlide
        message={deleteUserMessage}
        title="Cannot Delete"
        open={showDeleteProhibitedAlert}
        disableDelete
        cancelButtonText="Close"
        cancelClick={() => setshowDeleteProhibitedAlert(false)}
      />
      {/* ========== D E L E T E   A L E R T ========== */}
      <AlertDialogSlide
        message={deleteUserMessage}
        title="Confirm Delete"
        okButtonText="Delete"
        open={showDeleteAlert}
        cancelClick={() => setShowDeleteAlert(false)}
        okClick={handleDeleteConfirm}
      />
    </>
  )
}

export default Users
